import React from 'react';
import { Box, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Timestamp } from 'firebase/firestore';
import { toSafeDate } from '../../utils/appDateFunctions';

interface TimeRangeValue {
  start: Timestamp | null;
  end: Timestamp | null;
}

interface TimeRangeFieldProps {
  value: TimeRangeValue;
  onChange: (value: TimeRangeValue) => void;
  disabled?: boolean;
}

const TimeRangeField: React.FC<TimeRangeFieldProps> = ({ value, onChange, disabled }) => {
  const startValue = toSafeDate(value?.start);
  const endValue = toSafeDate(value?.end);

  const handleStartChange = (newValue: Date | null) => {
    onChange({
      ...value,
      start: newValue && !isNaN(newValue.getTime()) ? Timestamp.fromDate(newValue) : null,
    });
  };

  const handleEndChange = (newValue: Date | null) => {
    onChange({
      ...value,
      end: newValue && !isNaN(newValue.getTime()) ? Timestamp.fromDate(newValue) : null,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, sm: 3 },
          mt: 2,
        }}
      >
        <TimePicker
          value={startValue}
          onChange={handleStartChange}
          disabled={disabled}
          slotProps={{
            textField: {
              variant: 'standard',
              placeholder: 'Start time',
              sx: {
                minWidth: 140,
                '& .MuiInput-root': {
                  fontSize: '1.125rem',
                },
                '& .MuiInput-input': {
                  textAlign: 'center',
                  py: 1,
                },
              },
            },
          }}
        />
        <Typography color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          to
        </Typography>
        <TimePicker
          value={endValue}
          onChange={handleEndChange}
          disabled={disabled}
          slotProps={{
            textField: {
              variant: 'standard',
              placeholder: 'End time',
              sx: {
                minWidth: 140,
                '& .MuiInput-root': {
                  fontSize: '1.125rem',
                },
                '& .MuiInput-input': {
                  textAlign: 'center',
                  py: 1,
                },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default TimeRangeField;
